import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/translations';
import { useAuth } from '../context/AuthContext';
import { CITIES } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Building2, Upload } from 'lucide-react';

const ACTIVITY_CATEGORIES = ['Hoteli', 'Moteli i apartmani', 'Turistička naselja', 'Restorani', 'Aščinice i kafići', 'Odmarališta', 'Ostalo'];

export default function Registracija() {
  const { t } = useLang();
  const { registerTourist, registerSubject } = useAuth();
  const navigate = useNavigate();

  const [tourist, setTourist] = useState({ name: '', email: '', country: '', password: '', password2: '' });
  const [subject, setSubject] = useState({
    name: '', idNumber: '', city: '', address: '', postalCode: '', phone: '', email: '', activityCategory: '', password: '', docName: ''
  });

  const submitTourist = async (e) => {
    e.preventDefault();
    const { name, email, country, password, password2 } = tourist;
    if (!name || !email || !country || !password) { toast.error('Molimo popunite sva polja.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('Unesite ispravnu e-mail adresu.'); return; }
    if (password.length < 6) { toast.error('Lozinka mora imati najmanje 6 znakova.'); return; }
    if (password !== password2) { toast.error('Lozinke se ne podudaraju.'); return; }
    const res = await registerTourist({ name, email, country, password });
    if (!res.ok) { toast.error(res.error); return; }
    toast.success('Registracija uspješna! Dobrodošli.');
    navigate('/');
  };

  const submitSubject = async (e) => {
    e.preventDefault();
    const s = subject;
    if (!s.name || !s.idNumber || !s.city || !s.address || !s.postalCode || !s.phone || !s.email || !s.activityCategory || !s.password) {
      toast.error('Molimo popunite sva obavezna polja.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.email)) { toast.error('Unesite ispravnu e-mail adresu.'); return; }
    const res = await registerSubject(s);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success('Zahtjev za registraciju je poslan administratoru na odobrenje. Bit ćete obaviješteni e-mailom.');
    navigate('/prijava');
  };

  return (
    <div className="bg-[#f8f9fd] min-h-screen py-16">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <img src="/logo-mp.png" alt="Ministarstvo privrede" className="h-14 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0b1a3d]">{t('register')}</h1>
          <p className="text-sm text-slate-500 mt-1">IS E-Turizam – Srednjobosanski kanton</p>
        </div>

        <Tabs defaultValue="turista" className="w-full">
          <TabsList className="grid grid-cols-2 w-full bg-[#e8ecf7] h-11" data-testid="register-tabs">
            <TabsTrigger value="turista" data-testid="tab-turista" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white">
              <UserPlus className="h-4 w-4 mr-2" /> {t('touristReg')}
            </TabsTrigger>
            <TabsTrigger value="subjekat" data-testid="tab-subjekat" className="data-[state=active]:bg-[#001752] data-[state=active]:text-white">
              <Building2 className="h-4 w-4 mr-2" /> {t('subjectReg')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="turista">
            <form onSubmit={submitTourist} className="bg-white rounded-xl border border-slate-100 p-8 space-y-4" data-testid="tourist-form">
              <div>
                <Label className="text-slate-600 mb-1.5 block">{t('name')} *</Label>
                <Input value={tourist.name} onChange={(e) => setTourist({ ...tourist, name: e.target.value })} data-testid="tourist-name" className="border-slate-200" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t('email')} *</Label>
                  <Input type="email" value={tourist.email} onChange={(e) => setTourist({ ...tourist, email: e.target.value })} data-testid="tourist-email" className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t('country')} *</Label>
                  <Input value={tourist.country} onChange={(e) => setTourist({ ...tourist, country: e.target.value })} data-testid="tourist-country" className="border-slate-200" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t('password')} *</Label>
                  <Input type="password" value={tourist.password} onChange={(e) => setTourist({ ...tourist, password: e.target.value })} data-testid="tourist-password" className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t('repeatPassword')} *</Label>
                  <Input type="password" value={tourist.password2} onChange={(e) => setTourist({ ...tourist, password2: e.target.value })} data-testid="tourist-password2" className="border-slate-200" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md h-11 transition-colors duration-200" data-testid="tourist-submit">
                {t('register')}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="subjekat">
            <form onSubmit={submitSubject} className="bg-white rounded-xl border border-slate-100 p-8 space-y-4" data-testid="subject-form">
              <p className="text-xs text-slate-500 bg-[#f8f9fd] border border-slate-100 rounded-lg p-3">
                Registracija pružaoca usluge (hotel, restoran, apartman...). Zahtjev se šalje administratoru sistema na odobravanje.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Naziv / Ime i prezime *</Label>
                  <Input value={subject.name} onChange={(e) => setSubject({ ...subject, name: e.target.value })} data-testid="subject-name" className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">ID broj *</Label>
                  <Input value={subject.idNumber} onChange={(e) => setSubject({ ...subject, idNumber: e.target.value })} data-testid="subject-idnumber" className="border-slate-200" />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Grad *</Label>
                  <Select value={subject.city} onValueChange={(v) => setSubject({ ...subject, city: v })}>
                    <SelectTrigger className="border-slate-200" data-testid="subject-city"><SelectValue placeholder="Odaberite" /></SelectTrigger>
                    <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Ulica *</Label>
                  <Input value={subject.address} onChange={(e) => setSubject({ ...subject, address: e.target.value })} data-testid="subject-address" className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Poštanski broj *</Label>
                  <Input value={subject.postalCode} onChange={(e) => setSubject({ ...subject, postalCode: e.target.value })} data-testid="subject-postal" className="border-slate-200" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Broj telefona *</Label>
                  <Input value={subject.phone} onChange={(e) => setSubject({ ...subject, phone: e.target.value })} data-testid="subject-phone" className="border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t('email')} *</Label>
                  <Input type="email" value={subject.email} onChange={(e) => setSubject({ ...subject, email: e.target.value })} data-testid="subject-email" className="border-slate-200" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Kategorija djelatnosti *</Label>
                  <Select value={subject.activityCategory} onValueChange={(v) => setSubject({ ...subject, activityCategory: v })}>
                    <SelectTrigger className="border-slate-200" data-testid="subject-activity"><SelectValue placeholder="Odaberite" /></SelectTrigger>
                    <SelectContent>{ACTIVITY_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t('password')} *</Label>
                  <Input type="password" value={subject.password} onChange={(e) => setSubject({ ...subject, password: e.target.value })} data-testid="subject-password" className="border-slate-200" />
                </div>
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Dokumenti (potvrda registracije pravnog lica)</Label>
                <label className="flex items-center gap-3 border-2 border-dashed border-slate-200 rounded-lg p-4 cursor-pointer hover:border-[#001752]/40 transition-colors duration-200">
                  <Upload className="h-5 w-5 text-slate-400" />
                  <span className="text-sm text-slate-500">{subject.docName || 'Kliknite za odabir dokumenta (PDF, JPG)'}</span>
                  <input type="file" className="hidden" data-testid="subject-doc"
                    onChange={(e) => setSubject({ ...subject, docName: e.target.files?.[0]?.name || '' })} />
                </label>
              </div>
              <Button type="submit" className="w-full bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md h-11 transition-colors duration-200" data-testid="subject-submit">
                Pošalji zahtjev za registraciju
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <p className="text-sm text-slate-500 text-center mt-6">
          Već imate račun?{' '}
          <Link to="/prijava" className="text-[#001752] font-semibold hover:underline">{t('login')}</Link>
        </p>
      </div>
    </div>
  );
}
